import { type QuestionComments } from "@/components/DirectorySubmissions/Form/types";
import { useEffect, useState } from "react";

interface UseApprovalStatusProps {
  submissionComments: QuestionComments;
}

/**
 * Determine if a question ID is for a matter
 */
const isMatterQuestion = (questionId: string | undefined): boolean => {
  if (!questionId) return false;
  return questionId.startsWith("PM") || questionId.startsWith("CM");
};

/**
 * Hook to track and manage approval status of questions
 */
export const useApprovalStatus = ({
  submissionComments,
}: UseApprovalStatusProps) => {
  const [approvedQuestions, setApprovedQuestions] = useState<Set<string>>(
    new Set(),
  );
  const [pendingReviewQuestions, setPendingReviewQuestions] = useState<
    Set<string>
  >(new Set());

  useEffect(() => {
    const approved = new Set<string>();
    const pending = new Set<string>();

    // Group all comments by their effective question ID (position or matter ID)
    const commentsByEffectiveQuestion: Record<string, any[]> = {};

    // First pass: Group comments by effective question ID
    Object.entries(submissionComments).forEach(([commentKey, comments]) => {
      comments.forEach((comment) => {
        // For matter questions, use their position as the key (PM1, CM2)
        // For regular questions, use the question_id directly
        const effectiveQuestionId = isMatterQuestion(comment.question_id)
          ? comment.matter_id! // Already just the position without matter ID now
          : comment.question_id;

        if (!commentsByEffectiveQuestion[effectiveQuestionId]) {
          commentsByEffectiveQuestion[effectiveQuestionId] = [];
        }

        commentsByEffectiveQuestion[effectiveQuestionId].push(comment);
      });
    });

    // Second pass: Process approvals and review requests
    Object.entries(commentsByEffectiveQuestion).forEach(
      ([effectiveQuestionId, comments]) => {
        // Process comments for this effective question
        const approvalComments = comments.filter((c) => c.type === "approval");
        const reviewRequestComments = comments.filter(
          (c) => c.type === "review_request",
        );

        // Process approval status - just check if there are any approval comments
        if (approvalComments.length > 0) {
          approved.add(effectiveQuestionId);
        }

        // Process review request status - only if not already approved
        if (
          reviewRequestComments.length > 0 &&
          !approved.has(effectiveQuestionId)
        ) {
          pending.add(effectiveQuestionId);
        }
      },
    );

    setApprovedQuestions(approved);
    setPendingReviewQuestions(pending);
  }, [submissionComments]);

  const isQuestionApproved = (
    questionId: string,
    formData?: Record<string, any>,
  ): boolean => {
    // If this is a matter question, get the actual matter ID from formData
    const effectiveQuestionId =
      isMatterQuestion(questionId) && formData?.[questionId]?.id
        ? formData[questionId].id
        : questionId;

    const isApproved = approvedQuestions.has(effectiveQuestionId);

    return isApproved;
  };

  const isQuestionPendingReview = (
    questionId: string,
    formData?: Record<string, any>,
  ): boolean => {
    // If this is a matter question, get the actual matter ID from formData
    const effectiveQuestionId =
      isMatterQuestion(questionId) && formData?.[questionId]?.id
        ? formData[questionId].id
        : questionId;

    // If it's approved, it's not pending review
    if (isQuestionApproved(effectiveQuestionId, formData)) {
      return false;
    }

    const isPending = pendingReviewQuestions.has(effectiveQuestionId);

    return isPending;
  };

  return {
    approvedQuestions,
    pendingReviewQuestions,
    isQuestionApproved,
    isQuestionPendingReview,
  };
};
